import { useTranslation, t } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Table, Button, Space, Tag, Badge, message, Card, Tooltip, Popconfirm, Empty,
    Input, Popover, Typography, Progress, Grid, Segmented, theme, Spin, Switch
} from 'antd';
import {
    PoweroffOutlined, PlayCircleOutlined, ReloadOutlined, DesktopOutlined,
    PlusOutlined, SyncOutlined, SearchOutlined, LoadingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import evmService from '../../../shared/services/evmService';
import { VncConsole } from '../../../shared/components/console';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import { useRowContextMenu } from '../../../shared/components/RowActions';
import { localName } from '../../../shared/utils/format';
import { RenewModal, daysLeft } from '../../../shared/components/evm/RenewControls';

const { Text } = Typography;
const { useBreakpoint } = Grid;

// EVM 状态 → 标签（statusDictionary 无 evm 专用映射，这里就地定义）
const EVM_STATUS = {
    create: ['Creating', 'processing'],
    rebuild: ['Rebuilding', 'processing'],
    active: ['运行中', 'success'],
    suspend: ['Paused', 'warning'],
    flow_limit: ['Traffic limit exceeded', 'warning'],
    migrate: ['Migrating', 'processing'],
    delete: ['Deleting', 'default'],
    error: ['Error', 'error'],
    processing: ['Processing', 'processing'], // customers see error as this (ops gets the alert)
};
// 节点上报的电源状态（null = 未知）
const POWER = {
    running: ['运行中', 'success'],
    shutoff: ['Powered off', 'default'],
    paused: ['Suspended', 'warning'],
};
const powerKnown = (vm) => vm.power_state !== null && vm.power_state !== undefined && vm.power_state !== '';
// 某电源操作对该行是否可用：状态 active 且非 busy；电源状态已知时，开机仅非运行中、关机/重启仅运行中
const powerAllowed = (vm, action) => {
    if (vm.status !== 'active' || vm.busy) return false;
    if (!powerKnown(vm)) return true;
    return action === 'boot' ? vm.power_state !== 'running' : vm.power_state === 'running';
};
const powerBlockTip = (vm, action) => {
    if (vm.busy) return t('Task running');
    if (vm.status !== 'active') return t('Action unavailable in the current state');
    if (!powerAllowed(vm, action)) return action === 'boot' ? t('Powered on') : t('Powered off');
    return null;
};
const PowerBadge = ({ powerState }) => {
    const { t } = useTranslation();
    if (powerState === null || powerState === undefined || powerState === '') return null;
    const [label, status] = POWER[powerState] || [String(powerState), 'default'];
    return <Badge status={status} text={<span style={{ fontSize: 12 }}>{t(label)}</span>} />;
};

const EvmStatusTag = ({ status, busy, powerState }) => {
    const { t } = useTranslation();
    let [label, color] = EVM_STATUS[status] || [status || '-', 'default'];
    const known = powerState !== null && powerState !== undefined && powerState !== '';
    if (status === 'active' && known) label = 'Normal';
    return (
        <Space size={4}>
            <Tag color={color} style={{ marginInlineEnd: 0 }}>{t(label)}</Tag>
            {known && <PowerBadge powerState={powerState} />}
            {busy && (
                <Tooltip title={t('Task running')}>
                    <LoadingOutlined style={{ fontSize: 12 }} />
                </Tooltip>
            )}
        </Space>
    );
};

const fmtMb = (mb) => {
    const v = Number(mb) || 0;
    if (v >= 1024 * 1024) return `${(v / 1024 / 1024).toFixed(2)} TB`;
    if (v >= 1024) return `${(v / 1024).toFixed(1)} GB`;
    return `${v.toFixed(0)} MB`;
};

const specText = (vm) => {
    if (vm.pending) return [vm.product, `${vm.core}${t('Cores')} ${+(Number(vm.memory || 0) / 1024).toFixed(1)}G`].filter(Boolean).join(' · ');
    if (vm.plan) return localName(typeof vm.plan === 'object' ? (vm.plan.name || '-') : vm.plan);
    const mem = Number(vm.memory) || 0;
    const memText = mem >= 1024 ? `${+(mem / 1024).toFixed(1)}G` : `${mem}M`;
    return t('{{value}} cores {{memText}}{{value3}}', { value: vm.core || '-', memText: memText, value3: vm.disk_gb ? ` ${vm.disk_gb}G` : '' });
};

const ipsOf = (vm) => {
    const list = Array.isArray(vm.ips) ? vm.ips.filter(Boolean) : [];
    const primary = vm.ip || list[0] || vm.ip_address || null;
    const rest = list.filter((x) => x !== primary);
    return { primary, rest };
};

const IpCell = ({ vm }) => {
    const { primary, rest } = ipsOf(vm);
    if (!primary) return <Text type="secondary">-</Text>;
    return (
        <Space size={4} wrap={false}>
            <Text copyable={{ text: primary }} style={{ fontFamily: 'monospace', fontSize: 13 }}>{primary}</Text>
            {rest.length > 0 && (
                <Popover
                    trigger="click"
                    content={
                        <Space direction="vertical" size={2}>
                            {rest.map((ip) => (
                                <Text key={ip} copyable style={{ fontFamily: 'monospace', fontSize: 13 }}>{ip}</Text>
                            ))}
                        </Space>
                    }
                >
                    <Tag style={{ cursor: 'pointer', marginInlineEnd: 0 }}>+{rest.length}</Tag>
                </Popover>
            )}
        </Space>
    );
};

const TrafficCell = ({ traffic }) => {
    const { t } = useTranslation();
    const used = Number(traffic?.used_mb) || 0;
    const limit = Number(traffic?.limit_mb) || 0;
    if (!limit) {
        return <Text type="secondary" style={{ fontSize: 12 }}>{t('{{value}} / Unlimited', { value: fmtMb(used) })}</Text>;
    }
    const pct = Math.min(100, (used / limit) * 100);
    return (
        <Tooltip title={`${fmtMb(used)} / ${fmtMb(limit)}`}>
            <div style={{ minWidth: 110 }}>
                <Progress
                    percent={Number(pct.toFixed(1))}
                    size="small"
                    status={pct >= 100 ? 'exception' : pct >= 80 ? 'active' : 'normal'}
                    showInfo={false}
                    style={{ margin: 0 }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>{fmtMb(used)} / {fmtMb(limit)}</Text>
            </div>
        </Tooltip>
    );
};

const STATUS_FILTERS = [
    { label: 'All', value: 'all' },
    { label: '运行中', value: 'active' },
    { label: 'Processing', value: 'pending' },
    { label: 'Paused', value: 'suspend' },
    { label: 'Error', value: 'error' },
];
const PENDING_STATUSES = ['create', 'rebuild', 'migrate', 'delete', 'processing'];

const MyVms = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();

    const [vms, setVms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [kindFilter, setKindFilter] = useState('all');
    const [pending, setPending] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [acting, setActing] = useState({}); // { [vmId]: action }
    const [consoleModalVisible, setConsoleModalVisible] = useState(false);
    const [currentVm, setCurrentVm] = useState(null);
    const [vncData, setVncData] = useState(null);
    const [renewVmId, setRenewVmId] = useState(null);

    const loadVms = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const [response, pend] = await Promise.all([evmService.getVmList({ page: 1, limit: 500 }), evmService.getPendingProvisions()]);
            if (pend?.status === 'success') {
                const list = Array.isArray(pend.data) ? pend.data : (pend.data?.items || []);
                setPending(list.map((x) => ({ ...x, id: `p${x.id}`, pending: true })));
            }
            if (response.status === 'success') {
                const d = response.data;
                setVms(Array.isArray(d) ? d : (d?.items || []));
            } else {
                message.error(response.msg || response.error || t('Failed to load'));
            }
        } catch (error) {
            if (!silent) message.error(t('Failed to load'));
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => { loadVms(); }, [loadVms]);

    // Instances still being created / rebuilt / busy, or paid orders waiting: refresh until they settle.
    const settling = pending.length > 0 || vms.some((v) => ['create', 'rebuild', 'migrate'].includes(v.status) || v.busy);
    useEffect(() => {
        if (!settling) return undefined;
        const timer = setInterval(() => loadVms({ silent: true }), 5000);
        return () => clearInterval(timer);
    }, [settling, loadVms]);

    const goBuy = () => navigate(userPath('/evm/vms/purchase'));
    const goDetail = (vm) => navigate(userPath(`/evm/vms/${vm.id}`));

    const handlePower = async (vm, action) => {
        const names = { boot: t('Power on'), shutdown: t('Shut down'), reboot: t('Restart') };
        setActing((s) => ({ ...s, [vm.id]: action }));
        try {
            const response = await evmService.power(vm.id, action);
            if (response.status === 'success') {
                message.success(response.msg || t('{{value}} submitted', { value: names[action] }));
                setTimeout(loadVms, 2000);
            } else {
                message.error(response.msg || response.error || t('{{value}} failed', { value: names[action] }));
            }
        } catch (error) {
            message.error(t('{{value}} failed', { value: names[action] }));
        } finally {
            setActing((s) => { const n = { ...s }; delete n[vm.id]; return n; });
        }
    };

    const showConsole = async (vm) => {
        try {
            const response = await evmService.getNoVnc(vm.id);
            if (response.status === 'success' && response.data?.ws_url) {
                let wsUrl = response.data.ws_url;
                if (response.data.token && response.data.ts) {
                    const separator = wsUrl.includes('?') ? '&' : '?';
                    wsUrl += `${separator}token=${response.data.token}&ts=${response.data.ts}`;
                }
                setVncData({ wsUrl, password: response.data.password || '' });
                setCurrentVm(vm);
                setConsoleModalVisible(true);
            } else {
                message.error(response.msg || t('Failed to establish console connection'));
            }
        } catch (error) {
            message.error(t('Failed to open console'));
        }
    };

    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        const rows = ['all', 'pending'].includes(statusFilter) ? [...pending, ...vms] : vms;
        return rows.filter((vm) => {
            if (kindFilter !== 'all' && (vm.kind || 'cloud') !== kindFilter) return false;
            if (vm.pending) {
                if (!kw) return true;
            } else if (statusFilter === 'pending') {
                if (!PENDING_STATUSES.includes(vm.status)) return false;
            } else if (statusFilter === 'suspend') {
                if (!['suspend', 'flow_limit'].includes(vm.status)) return false;
            } else if (statusFilter !== 'all' && vm.status !== statusFilter) {
                return false;
            }
            if (!kw) return true;
            const hay = [vm.name, vm.hostname, vm.ip, vm.ip_address, ...(vm.ips || [])]
                .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(kw);
        });
    }, [vms, pending, statusFilter, keyword, kindFilter]);
    const hasVds = vms.some((vm) => vm.kind === 'vds') || pending.some((vm) => vm.kind === 'vds');

    const counts = useMemo(() => {
        const c = { all: vms.length + pending.length, active: 0, pending: pending.length, suspend: 0, error: 0 };
        vms.forEach((vm) => {
            if (vm.status === 'active') c.active++;
            else if (PENDING_STATUSES.includes(vm.status)) c.pending++;
            else if (['suspend', 'flow_limit'].includes(vm.status)) c.suspend++;
            else if (vm.status === 'error') c.error++;
        });
        return c;
    }, [vms, pending]);

    const hasExpiry = vms.some((vm) => vm.expires_at);

    const toggleAutoRenew = async (vm, v) => {
        const res = await evmService.setVmAutoRenew(vm.id, v);
        if (res?.status === 'success') setVms((list) => list.map((x) => (x.id === vm.id ? { ...x, auto_renew: v } : x)));
        else message.error(res?.msg || res?.error || t('Operation failed'));
    };

    // Expiry (red + days left within 7 days), auto-renew switch and 续费 for subscribed VMs.
    const renderExpiry = (vm) => {
        if (!vm.expires_at) return '-';
        const days = daysLeft(vm.expires_at);
        const soon = days <= 7;
        return (
            <Space direction="vertical" size={2} onClick={(e) => e.stopPropagation()}>
                <Space size={6}>
                    <Text type={soon ? 'danger' : undefined}>{String(vm.expires_at).slice(0, 10)}</Text>
                    {soon && <Tag color="error">{days > 0 ? t('{{n}} days', { n: days }) : t('Expired')}</Tag>}
                </Space>
                {vm.auto_renew !== null && vm.auto_renew !== undefined && (
                    <Space size={8}>
                        <Tooltip title={t('Auto-renewal')}>
                            <Switch size="small" checked={!!vm.auto_renew} onChange={(v) => toggleAutoRenew(vm, v)} />
                        </Tooltip>
                        <a onClick={() => setRenewVmId(vm.id)}>{t('Renew')}</a>
                    </Space>
                )}
            </Space>
        );
    };

    const renderActions = (vm) => {
        const busyAction = acting[vm.id];
        const can = (a) => powerAllowed(vm, a) && !busyAction;
        const wrap = (a, title, node) => <Tooltip title={powerBlockTip(vm, a) || title}>{node}</Tooltip>;
        return (
            <Space size={4} onClick={(e) => e.stopPropagation()}>
                {wrap('boot', t('Power on'), (
                    <Button
                        size="small" type="text" icon={<PlayCircleOutlined />}
                        disabled={!can('boot')}
                        loading={busyAction === 'boot'}
                        onClick={() => handlePower(vm, 'boot')}
                    />
                ))}
                <Popconfirm title={t('Shut down?')} okText={t('Shut down')} okType="danger" cancelText={t('Cancel')}
                    disabled={!can('shutdown')} onConfirm={() => handlePower(vm, 'shutdown')}>
                    {wrap('shutdown', t('Shut down'), (
                        <Button size="small" type="text" icon={<PoweroffOutlined />}
                            disabled={!can('shutdown')} loading={busyAction === 'shutdown'} />
                    ))}
                </Popconfirm>
                <Popconfirm title={t('Restart?')} okText={t('Restart')} cancelText={t('Cancel')}
                    disabled={!can('reboot')} onConfirm={() => handlePower(vm, 'reboot')}>
                    {wrap('reboot', t('Restart'), (
                        <Button size="small" type="text" icon={<ReloadOutlined />}
                            disabled={!can('reboot')} loading={busyAction === 'reboot'} />
                    ))}
                </Popconfirm>
                <Tooltip title={vm.status === 'active' ? t('Remote connection') : t('Cannot connect in the current state')}>
                    <Button size="small" type="text" icon={<DesktopOutlined />}
                        disabled={vm.status !== 'active'} onClick={() => showConsole(vm)} />
                </Tooltip>
            </Space>
        );
    };

    // 行右键 / 长按菜单：与操作列同一组电源操作
    const getRowActions = (vm) => {
        if (vm.pending) return [];
        const busyAction = acting[vm.id];
        const can = (a) => powerAllowed(vm, a) && !busyAction;
        return [
            { key: 'boot', label: t('Power on'), icon: <PlayCircleOutlined />, disabled: !can('boot'), onClick: () => handlePower(vm, 'boot') },
            { key: 'shutdown', label: t('Shut down'), icon: <PoweroffOutlined />, disabled: !can('shutdown'), confirm: { title: t('Shut down?'), okText: t('Shut down'), okType: 'danger', cancelText: t('Cancel') }, onClick: () => handlePower(vm, 'shutdown') },
            { key: 'reboot', label: t('Restart'), icon: <ReloadOutlined />, disabled: !can('reboot'), confirm: { title: t('Restart?'), okText: t('Restart'), cancelText: t('Cancel') }, onClick: () => handlePower(vm, 'reboot') },
            { key: 'console', label: t('Remote connection'), icon: <DesktopOutlined />, disabled: vm.status !== 'active', onClick: () => showConsole(vm) },
        ];
    };
    const rowMenu = useRowContextMenu(getRowActions);

    const columns = [
        {
            title: t('Name / Hostname'),
            key: 'name',
            render: (_, vm) => (vm.pending ? (
                <Space size={8} wrap>
                    <Text style={{ fontWeight: 500 }}>{vm.hostname || vm.product}</Text>
                    <Tag color="processing" style={{ marginInlineEnd: 0 }}>{t('Activating')}</Tag>
                    {vm.kind === 'vds' && <Tag color="purple" style={{ marginInlineEnd: 0 }}>VDS</Tag>}
                </Space>
            ) : (
                <Space direction="vertical" size={2}>
                    <Space size={8} wrap>
                        <a onClick={() => goDetail(vm)} style={{ fontWeight: 500 }}>{vm.name || `#${vm.id}`}</a>
                        <EvmStatusTag status={vm.status} busy={vm.busy} powerState={vm.power_state} />
                        {vm.kind === 'vds' && <Tag color="purple" style={{ marginInlineEnd: 0 }}>VDS</Tag>}
                    </Space>
                    {vm.hostname && vm.hostname !== vm.name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{vm.hostname}</Text>
                    )}
                </Space>
            )),
        },
        { title: 'IP', key: 'ip', render: (_, vm) => (vm.pending ? '-' : <IpCell vm={vm} />) },
        { title: t('Region'), dataIndex: 'region', key: 'region', render: (v) => v || '-' },
        { title: t('Configuration'), key: 'spec', render: (_, vm) => specText(vm) },
        { title: t('Traffic'), key: 'traffic', width: 150, render: (_, vm) => (vm.pending ? '-' : <TrafficCell traffic={vm.traffic} />) },
        ...(hasExpiry ? [{
            title: t('Expires'),
            dataIndex: 'expires_at',
            key: 'expires_at',
            render: (_, vm) => (vm.pending ? '-' : renderExpiry(vm)),
        }] : []),
        { title: t('Actions'), key: 'action', width: 150, render: (_, vm) => (vm.pending ? null : renderActions(vm)) },
    ];

    const emptyNode = (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={vms.length ? t('No matching instances') : t('No instances yet')}>
            {!vms.length && (
                <Button type="primary" icon={<PlusOutlined />} onClick={goBuy}>{t('Create instance')}</Button>
            )}
        </Empty>
    );

    const renderCards = () => {
        if (loading && !vms.length) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
        if (!filtered.length) return emptyNode;
        return (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {filtered.map((vm) => (vm.pending ? (
                    <Card key={vm.id} size="small" styles={{ body: { padding: 12 } }}>
                        <Space wrap>
                            <Text style={{ fontWeight: 500 }}>{vm.hostname || vm.product}</Text>
                            <Tag color="processing">{t('Activating')}</Tag>
                            {vm.kind === 'vds' && <Tag color="purple">VDS</Tag>}
                        </Space>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{specText(vm)}</Text></div>
                    </Card>
                ) : (
                    <Card key={vm.id} size="small" hoverable onClick={() => goDetail(vm)}
                        styles={{ body: { padding: 12 } }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {vm.name || `#${vm.id}`}
                                </div>
                                {vm.hostname && vm.hostname !== vm.name && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>{vm.hostname}</Text>
                                )}
                            </div>
                            <EvmStatusTag status={vm.status} busy={vm.busy} powerState={vm.power_state} />
                        </div>
                        <div style={{ marginTop: 8, fontSize: 13, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 4 }}
                            onClick={(e) => e.stopPropagation()}>
                            <Text type="secondary">IP</Text><IpCell vm={vm} />
                            <Text type="secondary">{t('Region')}</Text><span>{vm.region || '-'}</span>
                            <Text type="secondary">{t('Configuration')}</Text><span>{specText(vm)}</span>
                            <Text type="secondary">{t('Traffic')}</Text><TrafficCell traffic={vm.traffic} />
                            {vm.expires_at && (<><Text type="secondary">{t('Expires')}</Text>{renderExpiry(vm)}</>)}
                        </div>
                        <div style={{ marginTop: 8, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 6 }}>
                            {renderActions(vm)}
                        </div>
                    </Card>
                )))}
            </Space>
        );
    };

    return (
        <div>
            <PageHeader
                title={t('Cloud servers')}
                extra={
                    <>
                        <Button icon={<SyncOutlined />} onClick={loadVms} loading={loading} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={goBuy}>{t('Create instance')}</Button>
                    </>
                }
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ overflowX: 'auto', maxWidth: '100%', display: 'flex', gap: 8 }}>
                    {hasVds && (
                        <Segmented value={kindFilter} onChange={setKindFilter}
                            options={[{ value: 'all', label: t('All') }, { value: 'cloud', label: t('Cloud servers') }, { value: 'vds', label: 'VDS' }]} />
                    )}
                    <Segmented
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={STATUS_FILTERS
                            .filter((f) => f.value === 'all' || counts[f.value] > 0 || statusFilter === f.value)
                            .map((f) => ({ value: f.value, label: `${t(f.label)} ${counts[f.value] ?? 0}` }))}
                    />
                </div>
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={t('Name / IP')}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    style={{ width: isMobile ? '100%' : 240 }}
                />
            </div>

            {isMobile ? renderCards() : (
                <Card styles={{ body: { padding: 0 } }}>
                    <Table
                    onRow={rowMenu.onRow((r) => (r.pending ? {} : rowLink(navigate, (x) => `/evm/vms/${x.id}`)(r)))}
                    rowClassName={(r) => (r.pending ? '' : ROW_LINK_CLASS)}
                        columns={columns}
                        dataSource={filtered}
                        loading={loading}
                        rowKey="id"
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: emptyNode }}
                        pagination={filtered.length > 20 ? { pageSize: 20, showSizeChanger: true, showTotal: (total) => t('{{total}} total', { total: total }) } : false}
                    />
                    {rowMenu.menu}
                </Card>
            )}

            <RenewModal vmId={renewVmId} api={evmService} open={!!renewVmId}
                onClose={() => setRenewVmId(null)} onDone={loadVms} />

            {consoleModalVisible && vncData && (
                <VncConsole
                    title={currentVm?.hostname || currentVm?.name || 'VNC'}
                    wsUrl={vncData.wsUrl}
                    password={vncData.password}
                    onReconnect={() => currentVm && showConsole(currentVm)}
                    onClose={() => { setConsoleModalVisible(false); setVncData(null); }}
                    onError={(msg) => message.error(msg)}
                />
            )}
        </div>
    );
};

export default MyVms;
